import { useEffect } from 'react';
import { runInAction } from 'mobx';
// @ts-ignore
import html2pdf from 'html2pdf.js';

import '@components/Print/Print_.scss';

import { store } from '@store';

import { ENTRY_CONTENT_IN_VIEW_MODE_IDENTIFIER } from '@patient/store/entry/constants';
import { entryPrintFormService } from '@patient/services/entry/entryPrintFormService';
import { useCertificateData } from '@patient/ui/shared/Entry/hooks/useCertificateData';
import { ENTRY_FILE_DEFAULT_NAME } from '@store/types';
import { assertIsDefined } from '@utils/assertIsDefined';

interface Params {
    igrnoreLoadLegacy?: boolean;
}

interface Data {
    getEntryPdfWithEds(): Promise<ArrayBuffer>;
    getSoapEntryPdfWithEds(entryId: string): Promise<ArrayBuffer>;
}

export function useEntryPdfWithEds({ igrnoreLoadLegacy }: Params = {}): Data {
    const { certificate } = useCertificateData(true);
    const { entry, app, entryPage } = store;
    const { isViewMode } = entry;

    useEffect(() => {
        if (!igrnoreLoadLegacy && !entryPage.isRedesign) {
            runInAction(() => {
                app.isOnUpdate = true;
                entry.isViewMode = true;
                entry.isOnPrint = true;
            });

            const el = document.getElementById(ENTRY_CONTENT_IN_VIEW_MODE_IDENTIFIER);
            assertIsDefined(el, 'Entry content is rendered in view mode before it is printed');

            el.classList.add('print_root');

            return () => {
                el.classList.remove('print_root');
                runInAction(() => {
                    entry.isViewMode = isViewMode;
                    app.isOnUpdate = false;
                    entry.isOnPrint = false;
                    entryPage.isShortPrintForm = false;
                });
            };
        }

        return () => {};
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entryPage.isRedesign]);

    async function getSoapEntryPdfWithEds(entryId: string): Promise<ArrayBuffer> {
        return entryPrintFormService.getSignedEntryPdf(entryId ?? entry.id, { signature: certificate });
    }

    async function getEntryPdfWithEds(): Promise<ArrayBuffer> {
        return entryPage.isRedesign
            ? getSoapEntryPdfWithEds(entry.id)
            : html2pdf()
                  .from(document.getElementById(ENTRY_CONTENT_IN_VIEW_MODE_IDENTIFIER))
                  .set({
                      margin: 8,
                      filename: ENTRY_FILE_DEFAULT_NAME,
                      image: { type: 'png' },
                      jsPDF: { format: 'a4', orientation: 'portrait' },
                  })
                  .outputPdf('arraybuffer');
    }

    return {
        getSoapEntryPdfWithEds,
        getEntryPdfWithEds,
    };
}
